<template>
  <div class="admin-layout">
    <!-- Side Navigation Bar -->
    <Navbar/>

    <!-- Main Content Area -->
    <div class="admin-content">
      <header class="admin-header">
        <h1>Product Management</h1>
        <div class="user-profile">
          <span>Welcome, Admin</span>
          <i class="fas fa-user-circle"></i>
        </div>
      </header>

      <main class="dashboard">
        <!-- Stats Cards -->
        <section class="stats-cards">
          <div class="stat-card">
            <div class="stat-icon"><i class="fas fa-box"></i></div>
            <div class="stat-info">
              <h3>{{ products.length }}</h3>
              <p>Total Products</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon"><i class="fas fa-tags"></i></div>
            <div class="stat-info">
              <h3>{{ uniqueCategories.length }}</h3>
              <p>Categories</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon"><i class="fas fa-dollar-sign"></i></div>
            <div class="stat-info">
              <h3>${{ averagePrice.toFixed(2) }}</h3>
              <p>Avg. Price</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon"><i class="fas fa-plus-circle"></i></div>
            <div class="stat-info">
              <button @click="showAddModal = true" class="add-product-btn">Add New Product</button>
            </div>
          </div>
        </section>

        <!-- Product Table -->
        <section class="product-section">
          <div class="product-header">
            <h2>Products List</h2>
            <div class="search-bar">
              <input type="text" v-model="searchTerm" placeholder="Search products...">
              <i class="fas fa-search"></i>
            </div>
          </div>

          <div class="product-table">
            <table>
              <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Description</th>
                <th>Price</th>
                <th>Categories</th>
                <th>Date Added</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(product, index) in filteredProducts" :key="index">
                <td class="product-image">
                  <img :src="product.image || '/placeholder-image.jpg'" alt="Product image">
                </td>
                <td>{{ product.name }}</td>
                <td class="product-description">{{ truncateText(product.description, 50) }}</td>
                <td>${{ product.price }}</td>
                <td>
                  <div class="category-tags">
                      <span v-for="(category, idx) in product.categories" :key="idx" class="category-tag">
                        {{ category }}
                      </span>
                  </div>
                </td>
                <td>{{ formatDate(product.timestamp) }}</td>
                <td class="action-buttons">
                  <button @click="editProduct(index)" class="btn-edit">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button @click="confirmDelete(index)" class="btn-delete">
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>

    <!-- Add Product Modal -->
    <div class="modal" v-if="showAddModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Add New Product</h3>
          <button @click="showAddModal = false" class="close-modal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="product-name">Product Name</label>
            <input type="text" id="product-name" v-model="newProduct.name" placeholder="Enter product name">
          </div>

          <div class="form-group">
            <label for="product-description">Description</label>
            <textarea id="product-description" v-model="newProduct.description" placeholder="Enter product description"></textarea>
          </div>

          <div class="form-group">
            <label for="product-price">Price ($)</label>
            <input type="number" id="product-price" v-model="newProduct.price" step="0.01" min="0">
          </div>

          <div class="form-group">
            <label for="product-image">Image URL</label>
            <input type="text" id="product-image" v-model="newProduct.image" placeholder="Enter image URL">
          </div>

          <div class="form-group">
            <label for="product-categories">Categories</label>
            <div class="categories-input">
              <input
                  type="text"
                  v-model="categoryInput"
                  @keydown.enter="addCategory"
                  placeholder="Type and press Enter to add"
              >
              <div class="categories-tags">
                <span v-for="(category, idx) in newProduct.categories" :key="idx" class="category-tag">
                  {{ category }}
                  <i @click="removeCategory(idx)" class="fas fa-times"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showAddModal = false" class="btn-cancel">Cancel</button>
          <button @click="addProduct" class="btn-save">Save Product</button>
        </div>
      </div>
    </div>

    <!-- Edit Product Modal -->
    <div class="modal" v-if="showEditModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Edit Product</h3>
          <button @click="showEditModal = false" class="close-modal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="edit-product-name">Product Name</label>
            <input type="text" id="edit-product-name" v-model="editingProduct.name" placeholder="Enter product name">
          </div>

          <div class="form-group">
            <label for="edit-product-description">Description</label>
            <textarea id="edit-product-description" v-model="editingProduct.description" placeholder="Enter product description"></textarea>
          </div>

          <div class="form-group">
            <label for="edit-product-price">Price ($)</label>
            <input type="number" id="edit-product-price" v-model="editingProduct.price" step="0.01" min="0">
          </div>

          <div class="form-group">
            <label for="edit-product-image">Image URL</label>
            <input type="text" id="edit-product-image" v-model="editingProduct.image" placeholder="Enter image URL">
          </div>

          <div class="form-group">
            <label for="edit-product-categories">Categories</label>
            <div class="categories-input">
              <input
                  type="text"
                  v-model="editCategoryInput"
                  @keydown.enter="addEditCategory"
                  placeholder="Type and press Enter to add"
              >
              <div class="categories-tags">
                <span v-for="(category, idx) in editingProduct.categories" :key="idx" class="category-tag">
                  {{ category }}
                  <i @click="removeEditCategory(idx)" class="fas fa-times"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showEditModal = false" class="btn-cancel">Cancel</button>
          <button @click="updateProduct" class="btn-save">Update Product</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal" v-if="showDeleteModal">
      <div class="modal-content delete-modal">
        <div class="modal-header">
          <h3>Confirm Delete</h3>
          <button @click="showDeleteModal = false" class="close-modal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete the product "<strong>{{ products[deleteIndex]?.name }}</strong>"?</p>
          <p class="warning-text">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button @click="showDeleteModal = false" class="btn-cancel">Cancel</button>
          <button @click="deleteProduct" class="btn-delete">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, orderBy, query, Timestamp, deleteDoc, doc, addDoc, updateDoc } from 'firebase/firestore'
import Navbar from '../Header/Navbar.vue';
import {db} from "@/firebase";

export default {
  name: 'AdminProducts',
  components: {
    Navbar
  },
  data() {



    return {
      products: [],
      newProduct: {
        name: '',
        description: '',
        price: 0,
        image: '',
        categories: [],
        timestamp: Timestamp.now()
      },
      editingProduct: {
        name: '',
        description: '',
        price: 0,
        image: '',
        categories: [],
        timestamp: Timestamp.now()
      },
      categoryInput: '',
      editCategoryInput: '',
      searchTerm: '',
      showAddModal: false,
      showEditModal: false,
      showDeleteModal: false,
      editIndex: -1,
      deleteIndex: -1,
    };
  },
  computed: {
    filteredProducts() {
      if (!this.searchTerm) return this.products;

      const term = this.searchTerm.toLowerCase();
      return this.products.filter(product =>
          product.name.toLowerCase().includes(term) ||
          product.description.toLowerCase().includes(term) ||
          product.categories.some(cat => cat.toLowerCase().includes(term))
      );
    },
    uniqueCategories() {
      const categories = new Set();
      this.products.forEach(product => {
        product.categories.forEach(category => categories.add(category));
      });
      return Array.from(categories);
    },
    averagePrice() {
      if (this.products.length === 0) return 0;
      const total = this.products.reduce((sum, product) => sum + parseFloat(product.price), 0);
      return total / this.products.length;
    }
  },
  metaInfo() {
    return {
      title: "Product Management | ShopT"
    };
  },
  async created() {
    await this.fetchKits()
  },
  methods: {
    async fetchKits() {
      try {
        const q = query(collection(db, 'kits'), orderBy('timestamp', 'desc'))
        const snapshot = await getDocs(q)
        this.products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          quantity: 1,
        }))
      } catch (error) {
        console.error('Kits çekilemedi:', error)
      }
    },
    formatDate(timestamp) {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    },
    truncateText(text, length) {
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    addCategory() {
      if (this.categoryInput.trim() && !this.newProduct.categories.includes(this.categoryInput.trim())) {
        this.newProduct.categories.push(this.categoryInput.trim());
        this.categoryInput = '';
      }
    },
    removeCategory(index) {
      this.newProduct.categories.splice(index, 1);
    },
    addEditCategory() {
      if (this.editCategoryInput.trim() && !this.editingProduct.categories.includes(this.editCategoryInput.trim())) {
        this.editingProduct.categories.push(this.editCategoryInput.trim());
        this.editCategoryInput = '';
      }
    },
    removeEditCategory(index) {
      this.editingProduct.categories.splice(index, 1);
    },
    resetNewProduct() {
      this.newProduct = {
        name: '',
        description: '',
        price: 0,
        image: '',
        categories: [],
        timestamp: Timestamp.now()
      };
      this.categoryInput = '';
    },
    async addProduct() {
      if (!this.newProduct.name) {
        alert('Product name is required');
        return;
      }

      try {
        const newDoc = await addDoc(collection(db, 'kits'), {
          ...this.newProduct,
          price: parseFloat(this.newProduct.price),
          timestamp: Timestamp.now()
        });

        this.products.unshift({
          id: newDoc.id,
          ...this.newProduct,
          price: parseFloat(this.newProduct.price),
          timestamp: Timestamp.now()
        });

        this.showAddModal = false;
        this.resetNewProduct();
      } catch (error) {
        console.error('Ürün eklenemedi:', error);
      }
    },
    editProduct(index) {
      this.editIndex = index;
      this.editingProduct = { ...this.products[index] };
      this.showEditModal = true;
    },
    async updateProduct() {
      if (!this.editingProduct.name) {
        alert('Product name is required');
        return;
      }

      try {
        const productRef = doc(db, 'kits', this.editingProduct.id);
        await updateDoc(productRef, {
          ...this.editingProduct,
          price: parseFloat(this.editingProduct.price),
          timestamp: Timestamp.now()
        });

        if (this.editIndex > -1) {
          this.products[this.editIndex] = {
            ...this.editingProduct,
            price: parseFloat(this.editingProduct.price),
            timestamp: Timestamp.now()
          };
        }

        this.showEditModal = false;
      } catch (error) {
        console.error('Ürün güncellenemedi:', error);
      }
    },
    confirmDelete(index) {
      this.deleteIndex = index;
      this.showDeleteModal = true;
    },
    async deleteProduct() {
      if (this.deleteIndex > -1) {
        try {
          const productId = this.products[this.deleteIndex].id;
          await deleteDoc(doc(db, 'kits', productId));
          this.products.splice(this.deleteIndex, 1); // localden de sil
          this.showDeleteModal = false;
          this.deleteIndex = -1;
        } catch (error) {
          console.error('Ürün silinemedi:', error);
        }
      }
    }
  }
};
</script>

<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  background-color: #111;
  color: #fff;
}

/* Main Content Styles */
.admin-content {
  flex: 1;
  padding: 0;
  overflow-y: auto;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background-color: #111;
  border-bottom: 1px solid #333;
}

.admin-header h1 {
  font-size: 1.8rem;
  font-weight: 500;
  margin: 0;
}

.user-profile {
  display: flex;
  align-items: center;
}

.user-profile span {
  margin-right: 10px;
  font-weight: 500;
}

.user-profile i {
  font-size: 1.5rem;
  color: #ccc;
}

/* Dashboard Styles */
.dashboard {
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 10px;
}

.stat-card {
  background-color: #222;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  border: 1px solid #444;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  background-color: purple;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.stat-icon i {
  font-size: 1.5rem;
  color: #fff;
}

.stat-info h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: #fff;
}

.stat-info p {
  font-size: 0.9rem;
  color: #999;
  margin: 5px 0 0 0;
}

.add-product-btn {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;
}

.add-product-btn:hover {
  background-color: #45a049;
}

/* Product Section */
.product-section {
  background-color: #222;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #444;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.product-header h2 {
  font-size: 1.5rem;
  margin: 0;
  font-weight: 500;
}

.search-bar {
  position: relative;
  width: 300px;
}

.search-bar input {
  width: 100%;
  padding: 10px 15px;
  padding-right: 40px;
  border-radius: 5px;
  border: 1px solid #444;
  background-color: #333;
  color: #fff;
}

.search-bar i {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #777;
}

.product-table {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background-color: #333;
}

th, td {
  padding: 12px 15px;
  text-align: left;
}

th {
  font-weight: 500;
  color: #ccc;
}

tbody tr {
  border-bottom: 1px solid #444;
}

tbody tr:last-child {
  border-bottom: none;
}

.product-image img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 5px;
}

.product-description {
  max-width: 200px;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.category-tag {
  background-color: rgba(128, 0, 128, 0.2);
  color: #ee00af;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
}

.category-tag i {
  margin-left: 5px;
  cursor: pointer;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons button {
  border: none;
  background: none;
  color: #ccc;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-edit {
  background-color: rgba(241, 196, 15, 0.2);
}

.btn-edit:hover {
  background-color: rgba(241, 196, 15, 0.4);
  color: #f1c40f;
}

.btn-delete {
  background-color: rgba(231, 76, 60, 0.2);
}

.btn-delete:hover {
  background-color: rgba(231, 76, 60, 0.4);
  color: #e74c3c;
}

/* Modal Styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #222;
  border-radius: 10px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.delete-modal {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #444;
}

.modal-header h3 {
  margin: 0;
  font-weight: 500;
}

.close-modal {
  background: none;
  border: none;
  color: #ccc;
  font-size: 1.2rem;
  cursor: pointer;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #444;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #444;
  background-color: #333;
  color: #fff;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.categories-input {
  margin-top: 5px;
}

.categories-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
}

.warning-text {
  color: #e74c3c;
  font-style: italic;
}

.btn-save,
.btn-cancel,
.btn-delete {
  padding: 8px 15px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.btn-cancel {
  background-color: #555;
  color: #fff;
}

.btn-save {
  background-color: #4CAF50;
  color: #fff;
}

.btn-delete {
  background-color: #e74c3c;
  color: #fff;
}

@media (max-width: 768px) {
  .admin-layout {
    flex-direction: column;
  }

  .stats-cards {
    grid-template-columns: 1fr;
  }

  .product-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .search-bar {
    width: 100%;
  }

  .dashboard {
    padding: 15px;
  }
}
</style>